import { PackagePlus } from 'lucide-react';
import React from 'react';
import { StepName } from '|/app/services';
import LinkButton from '|/components/carvana-ds/link-button';
import useOrderSummaryVm from '|/components/carvana-ds/order-summary-display/use-order-summary-vm';
import currency from '|/helpers/currency';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import useNewCar from '|/hooks/use-new-car';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { AmountRow } from './amount-row';
import PriceSection from './price-section';
import { CoverageRow, PriceDetailsView } from '../derive-price-details';
import styles from '../price-details.module.css';

interface YourSelectionsSectionProps {
  summary: ReviewSummaryResponse;
  view: PriceDetailsView;
  onViewCarvanaCare: () => void;
  onViewGap: () => void;
}

interface CoverageProps {
  row: CoverageRow;
  onViewDetails: () => void;
  onAdd: () => void;
  addLabel: string;
  viewDetailsAnalyticsId: string;
  addAnalyticsId: string;
  testId: string;
}

const Coverage = ({
  row,
  onViewDetails,
  onAdd,
  addLabel,
  viewDetailsAnalyticsId,
  addAnalyticsId,
  testId
}: CoverageProps) => (
  <div className={styles.coverageRow} data-testid={testId}>
    <div className={styles.coverageLabel}>
      <p className={styles.rowLabel}>{row.label}</p>
      {row.coverage && <p className={styles.coverageCopy}>{row.coverage}</p>}
      <LinkButton
        size="xs"
        underline={false}
        onClick={row.isDeclined ? onAdd : onViewDetails}
        data-analytics-id={row.isDeclined ? addAnalyticsId : viewDetailsAnalyticsId}
      >
        {row.isDeclined ? addLabel : 'View details'}
      </LinkButton>
    </div>
    <div className={styles.coverageAmount}>
      <p className={styles.rowAmount}>{row.isDeclined ? 'Declined' : currency(row.amount)}</p>
      {!row.isDeclined && row.monthlyDelta !== null && (
        <p className={styles.coverageMonthly}>
          {currency(row.monthlyDelta, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo
        </p>
      )}
    </div>
  </div>
);

const YourSelectionsSection = ({
  summary,
  view,
  onViewCarvanaCare,
  onViewGap
}: YourSelectionsSectionProps) => {
  const navigateToStep = useNavigateToStep();
  const { trackEvent } = useAnalyticsContext();
  const { getDealershipDisplayName } = useNewCar();
  const { transferAddress, isVendingMachine, vehicleType } = useOrderSummaryVm(summary);
  const { transferLabel, transferAmount } = view;

  // Only pickup names its location; delivery shows the address
  // alone. Mirrors review-order's getting-vehicle-card.tsx — the summary payload has no
  // location-name field, so every surface derives it from the city the same way.
  const pickupLocationName = isVendingMachine
    ? `${transferAddress?.city} Vending Machine`
    : vehicleType?.toLowerCase() === 'new'
      ? getDealershipDisplayName(transferAddress?.city ?? '', transferAddress?.city)
      : 'Carvana pickup location';

  return (
    <PriceSection
      icon={<PackagePlus size={16} color="var(--icon-strong)" />}
      title="Your selections"
      testId="pp_price-details_your-selections"
    >
      {transferLabel && (
        <div className={styles.coverageRow} data-testid="pp_price-details_transfer">
          <div className={styles.coverageLabel}>
            <p className={styles.rowLabel}>{transferLabel}</p>
            {transferAddress && (
              <p className={styles.coverageCopy}>
                {transferLabel === 'Pickup' && (
                  <>
                    {pickupLocationName}
                    <br />
                  </>
                )}
                {transferAddress.line1}
                {transferAddress.line2 ? ` ${transferAddress.line2}` : ''}
                <br />
                {transferAddress.city}, {transferAddress.state} {transferAddress.zipCode}
              </p>
            )}
            <LinkButton
              size="xs"
              underline={false}
              onClick={() => {
                navigateToStep(StepName.PickupDelivery);
              }}
              data-analytics-id="pp_review_order_breakdown_edit_pickup_delivery"
            >
              Edit {transferLabel.toLowerCase()}
            </LinkButton>
          </div>
          <p className={styles.rowAmount}>{transferAmount ? currency(transferAmount) : 'FREE'}</p>
        </div>
      )}

      {view.carvanaCare && (
        <Coverage
          row={view.carvanaCare}
          onViewDetails={() => {
            // Control's sheet fires this legacy event from both CarvanaCare links — kept for
            // 1:1 funnel comparison.
            trackEvent('review order vsc clicked');
            onViewCarvanaCare();
          }}
          onAdd={() => {
            trackEvent('review order vsc clicked');
            navigateToStep(StepName.VSC);
          }}
          addLabel="Add CarvanaCare"
          viewDetailsAnalyticsId="pp_review_order_summary_view_carvanacare_details"
          addAnalyticsId="pp_review_order_summary_add_carvanacare"
          testId="pp_price-details_carvanacare"
        />
      )}

      {view.gap && (
        <Coverage
          row={view.gap}
          onViewDetails={onViewGap}
          onAdd={() => {
            navigateToStep(StepName.Gap);
          }}
          addLabel="Add GAP coverage"
          viewDetailsAnalyticsId="pp_review_order_summary_view_gap_details"
          addAnalyticsId="pp_review_order_summary_edit_protection"
          testId="pp_price-details_gap"
        />
      )}

      <AmountRow
        label="Total purchase price"
        amount={currency(view.totalPurchasePrice)}
        emphasis="total"
      />
    </PriceSection>
  );
};

export default YourSelectionsSection;
